import { Location } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { GenericResponse, IAccount, ICampaign, IPaginated, TypeContract } from '@interfaces';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UtilService } from 'src/app/services/util.service';
import { IHeaderData } from 'src/app/shared/header-data/header-data.models';
import { ToastrService } from 'src/app/shared/ui/toastr/toastr.service';
import { Select } from '@interfaces';
import { DownloadOfferDialogComponent } from './download-offer-dialog/download-offer-dialog.component';
import { NewOfferService } from './new-offer.service';

@Component({
  selector: 'app-new-offer',
  templateUrl: './new-offer.component.html',
  styleUrls: ['./new-offer.component.scss']
})
export class NewOfferComponent implements OnInit, OnDestroy {
  public headerCol: IHeaderData;
  public newOfferForm: FormGroup;
  public accountDisabled = false;
  public businessDisabled = true;
  public installationDisabled = true;
  public renewalDisabled = true;
  public dutyDisabled = true;
  public campaignDisabled = true;
  public levelDisabled = true;
  public accountList: Select[];
  public businessList = [];
  public installationList = [];
  public defaultInstallationAll = '0';
  public campaignsList = [];
  public campaignsRes: ICampaign[];
  public levelList = [];
  public isLoading = true;
  public typeContract: Select[] = [];
  private destroyed$ = new Subject<boolean>();

  constructor(
    private location: Location,
    private formBuilder: FormBuilder,
    private newOfferService: NewOfferService,
    private toastrService: ToastrService,
    private dialog: MatDialog,
    private utilService: UtilService
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.buildHeader(true);
    this.getDataDropowns();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private buildHeader(disabled: boolean): void {
    this.headerCol = {
      title: 'NEW_OFFER.TITLE',
      type: 'buttons',
      data: [
        {
          label: 'COMMONS.READY',
          action: 'createOffer',
          disabled
        }
      ]
    };
  }

  public handleButton(action: string): void {
    if (action === 'goBack') {
      this.location.back();
    } else {
      this.isLoading = true;
      const body = this.newOfferForm.value;
      if (body.codesRazonSocial[0] === 'allOptions') {
        body.codesRazonSocial = body.codesRazonSocial.slice(1);
      }
      if (body.codesInstalacion[0] === 'allOptions') {
        body.codesInstalacion = body.codesInstalacion.slice(1);
      }
      this.newOfferService.setOffer(body).pipe(takeUntil(this.destroyed$)).subscribe(
        res => {
          this.isLoading = false;
          if (res.success) {
            this.openDialog(res.data);
          } else {
            this.utilService.toastErrors(res);
          }
        },
        error => {
          this.isLoading = false;
          this.utilService.toastErrors(error);
        }
      );
    }
  }

  public openDialog(offerCode: string[]): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      autoFocus: false,
      width: '575px',
      height: '281px'
    };
    const dialogRef = this.dialog.open(DownloadOfferDialogComponent, dialogConfig.data);
    dialogRef.afterClosed().subscribe(result => {
      if (result?.event === 'download') {
        this.isLoading = true;
        this.toastrService.error('COMMONS.DOWNLOADING');
        this.newOfferService.getDocument(offerCode).pipe(takeUntil(this.destroyed$)).subscribe(
          (response: Blob) => {
            if (response.type !== 'application/json') {
              const downloadURL = URL.createObjectURL(response);
              this.toastrService.success('NEW_OFFER.OFFER_DOWNLOAD');
              window.open(downloadURL);
              this.isLoading = false;
              this.location.back();
            } else {
              const reader = new FileReader();
              reader.addEventListener('loadend', e => {
                this.toastrService.error(JSON.parse(e.target.result as string).Message);
                this.isLoading = false;
                this.location.back();
              });
              reader.readAsText(response);
            }
          },
          error => {
            this.utilService.toastErrors(error);
            this.isLoading = false;
            this.location.back();
          }
        );
      } else {
        this.toastrService.error('NEW_OFFER.CREATION_SUCCESSFUL');
      }
    });
  }

  public initForm(): void {
    this.newOfferForm = this.formBuilder.group({
      codCuentaNegociadora: ['', [Validators.required]],
      codesRazonSocial: [[], [Validators.required]],
      codesInstalacion: [[]],
      nombreOferta: [{ value: '', disabled: true }, [Validators.required, Validators.maxLength(50)]],
      renovacionTacita: [{ value: null, disabled: true }, [Validators.required]],
      impuestoElectrico: [{ value: null, disabled: true }, [Validators.required]],
      codigoCampania: [[], [Validators.required]],
      nombreNivel: [[], [Validators.required]],
      todasRazonesSociales: [false, [Validators.required]],
      todasInstalaciones: [false, [Validators.required]],
      tipoContrato: [null, Validators.required]
    });
    this.tradingAccountStatusChange();
    this.bussinesNameStatusChange();
    this.installationCodesStatusChange();
    this.campaignCodeStatusChange();
    this.newOfferFormStatusChange();
  }

  private getDataDropowns(): void {
    this.isLoading = true;
    forkJoin([
      this.newOfferService.getAccounts(),
      this.newOfferService.getTypeContract()
    ]).pipe(takeUntil(this.destroyed$)).subscribe(
      ([account, typeContract]) => {
        this.getAccountsResponse(account);
        this.getTypeContracResponse(typeContract);
        this.isLoading = false;
      },
      error => {
        this.isLoading = false;
        this.utilService.toastErrors(error);
      }
    );
  }

  private getAccountsResponse(response: GenericResponse<IPaginated<IAccount>>): void {
    if (response.success) {
      this.accountList = this.genericMapper('nombreCuentaNegociadora', 'codCuentaNegociadora', response.data.values);
    } else {
      this.utilService.toastErrors(response);
    }
  }


  private getTypeContracResponse(response: GenericResponse<TypeContract>): void {
    if (response.success) {
      this.typeContract = this.genericMapper('descripcion', 'codigo', response.data);
    } else {
      this.utilService.toastErrors(response);
    }
  }

  public genericMapper(label: string, value: string | number, values: any = []): any[] {
    const aux = [];
    values.forEach(element => {
      aux.push({ label: element[label], value: element[value] });
    });
    return aux;
  }

  public disabledDataFields(clearLists: boolean = true): void {
    this.newOfferForm.patchValue({
      codigoCampania: null,
      nombreNivel: null
    });
    this.campaignDisabled = true;
    this.levelDisabled = true;
    if (clearLists) {
      this.campaignsList = [];
      this.levelList = [];
    }
    this.newOfferForm.updateValueAndValidity();
  }

  public tradingAccountStatusChange(): void {
    this.newOfferForm.controls.codCuentaNegociadora.statusChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.isLoading = true;
      this.businessList = null;
      this.newOfferForm.get('codesRazonSocial').setValue([]);
      this.newOfferForm.get('codesInstalacion').setValue([]);
      this.newOfferForm.updateValueAndValidity();
      this.newOfferService.getBusiness(
        { CodCuentaNegociadora: this.newOfferForm.controls.codCuentaNegociadora.value }
      ).pipe(takeUntil(this.destroyed$)).subscribe(
        res => {
          this.isLoading = false;
          if (res.success) {
            this.businessList = this.genericMapper('nombreRazonSocial', 'codRazonSocial', res.data.values);
            this.businessDisabled = false;
            this.installationList = [];
            this.installationDisabled = true;
            this.newOfferForm.controls.codesInstalacion.patchValue([]);
            this.newOfferForm.updateValueAndValidity();
            this.disabledDataFields();
          } else {
            this.utilService.toastErrors(res);
          }
        },
        error => {
          this.isLoading = false;
          this.utilService.toastErrors(error);
        }
      );
    });
  }

  public bussinesNameStatusChange(): void {
    this.newOfferForm.controls.codesRazonSocial.statusChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      const formControl = this.newOfferForm.controls;
      let codesRazonSocialLength = formControl.codesRazonSocial.value?.length;
      let codesSocialReason = formControl.codesRazonSocial.value;
      if (codesSocialReason[0] === 'allOptions') {
        codesSocialReason = codesSocialReason.slice(1);
        codesRazonSocialLength = codesRazonSocialLength - 1;
      }
      if (codesRazonSocialLength > 0) {
        this.isLoading = true;
        formControl.todasRazonesSociales.patchValue(codesRazonSocialLength === this.businessList.length);
        if (codesRazonSocialLength > 1) {
          formControl.todasInstalaciones.patchValue(true);
          this.installationDisabled = false;
        } else {
          this.newOfferForm.patchValue({
            todasRazonesSociales: false,
            codesInstalacion: [],
            todasInstalaciones: false
          });
        }
        const subInstallations = this.newOfferService.getInstallations({
          CodCuentaNegociadora: formControl.codCuentaNegociadora.value,
          CodesRazonSocial: codesSocialReason
        });
        forkJoin({ res1: subInstallations }).pipe(takeUntil(this.destroyed$)).subscribe(
          ({ res1 }) => {
            this.isLoading = false;
            if (res1.success) {
              this.installationList = this.genericMapper('nombreInstalacion', 'codInstalacion', res1.data.values);
              if (formControl.todasInstalaciones.value) {
                formControl.codesInstalacion.patchValue(this.installationList.map(el => el.value));
                this.installationDisabled = true;
              } else {
                this.installationDisabled = false;
              }
            } else {
              this.utilService.toastErrors(res1);
            }
            this.newOfferForm.updateValueAndValidity();
          },
          error => {
            this.isLoading = false;
            this.utilService.toastErrors(error);
          }
        );
      } else {
        this.installationDisabled = true;
        this.installationList = [];
        formControl.codesInstalacion.patchValue([]);
        this.disabledDataFields();
      }
    });
  }

  public installationCodesStatusChange(): void {
    this.newOfferForm.controls.codesInstalacion.statusChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      const formControl = this.newOfferForm.controls;
      let codesInstallationLength = formControl.codesInstalacion.value?.length;
      let installations = formControl.codesInstalacion.value;
      let codesSocialReason = formControl.codesRazonSocial.value;
      if (installations[0] === 'allOptions') {
        installations = installations.slice(1);
        codesInstallationLength = codesInstallationLength - 1;
      }
      if (codesSocialReason[0] === 'allOptions') {
        codesSocialReason = codesSocialReason.slice(1);
      }
      if (codesInstallationLength > 0) {
        formControl.todasInstalaciones.patchValue(codesInstallationLength === this.installationList.length);
        this.newOfferService.getCampaigns(
          {
            CodInstalaciones: installations || null,
            CodRazonesSociales: codesSocialReason || null,
            CodCuentaNegociadora: formControl.codCuentaNegociadora.value || null,
          }
        ).subscribe(res => {
          if (res.success) {
            this.campaignsRes = res.data;
            this.campaignsList = this.genericMapper('nombreCampania', 'codCampania', res.data);
          } else {
            this.utilService.toastErrors(res);
          }
        }, error => {
          this.utilService.toastErrors(error);
        });
        this.newOfferForm.controls.nombreOferta.enable();
        this.newOfferForm.controls.renovacionTacita.enable();
        this.newOfferForm.controls.impuestoElectrico.enable();
        this.campaignDisabled = false;
        this.dutyDisabled = false;
        this.levelDisabled = false;
      } else {
        this.disabledDataFields(false);
      }
    });
  }

  public campaignCodeStatusChange(): void {
    this.newOfferForm.controls.codigoCampania.statusChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.levelList = this.genericMapper(
        'nombreNivel',
        'nombreNivel',
        this.campaignsRes?.find(el => el.codCampania === this.newOfferForm.controls.codigoCampania.value)?.niveles
      );
    });
  }

  public newOfferFormStatusChange(): void {
    this.newOfferForm.statusChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
      if (value === 'VALID') {
        this.buildHeader(false);
      } else {
        this.buildHeader(true);
      }
    });
  }
}
